/**
 * Tabla de stakeholders (§7).
 *
 * El filtrado, el orden y la paginación son del servidor, no de la tabla: el
 * backend ya sabe hacerlos sobre índices y una entidad puede traer cientos de
 * filas. TanStack Table se usa en modo manual, solo para la estructura de
 * columnas, la selección y las filas expandibles.
 */

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from '@tanstack/react-table'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronRight as Siguiente,
  Search,
  X,
} from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'

import { useEnums, useTabla } from '../api/hooks'
import { CeldaEnum, CeldaTexto } from './curaduria/CeldaEditable'
import type { FiltrosTabla, Nivel, StakeholderFila } from '../api/tipos'
import { NIVELES, ETIQUETA_NIVEL } from '../ui/paleta'
import {
  Boton,
  Entrada,
  EscaleraNivel,
  EtiquetaPostura,
  EtiquetaTipo,
  Error as EstadoError,
  EsqueletoTabla,
  Interruptor,
  MarcaCorroborado,
  MarcaEdicion,
  Seleccion,
  Vacio,
} from '../ui/primitivos'
import { cx } from '../ui/util'

const POR_PAGINA = 50

/** Clases que decide cada columna, sobre todo para ocultarse en pantallas
 *  estrechas antes que estrangular la columna del nombre. */
interface MetaColumna {
  clase?: string
}

function CabeceraOrdenable({
  etiqueta,
  campo,
  orden,
  onOrden,
  alineado = 'left',
}: {
  etiqueta: string
  campo: string
  orden: string | undefined
  onOrden: (valor: string | undefined) => void
  alineado?: 'left' | 'right' | 'center'
}) {
  const activo = orden === campo || orden === `-${campo}`
  const descendente = orden === `-${campo}`

  return (
    <button
      onClick={() =>
        onOrden(
          activo && descendente
            ? undefined
            : descendente
              ? campo
              : activo
                ? `-${campo}`
                : campo,
        )
      }
      className={cx(
        'inline-flex w-full items-center gap-1 text-[11px] font-semibold tracking-wide uppercase',
        activo ? 'text-[var(--color-acento)]' : 'text-[var(--color-texto-tenue)]',
        alineado === 'right' && 'justify-end',
        alineado === 'center' && 'justify-center',
        'hover:text-[var(--color-texto)]',
      )}
      title={`Ordenar por ${etiqueta.toLowerCase()}`}
    >
      {etiqueta}
      {activo && (descendente ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
    </button>
  )
}

function DetalleExpandido({ fila }: { fila: StakeholderFila }) {
  const bloques: [string, string[]][] = [
    ['Intereses', fila.intereses],
    ['Riesgos o tensiones', fila.riesgos_o_tensiones],
    ['Mecanismos de relacionamiento', fila.mecanismos_de_relacionamiento],
    ['Territorios', fila.territorios],
  ]
  const conContenido = bloques.filter(([, valores]) => valores.length > 0)

  if (!conContenido.length) {
    return (
      <p className="px-3 py-2 text-[12px] text-[var(--color-texto-tenue)]">
        Esta ficha no tiene listas de detalle. Ábrela en el panel lateral para ver su
        descripción y fuentes.
      </p>
    )
  }

  return (
    <div className="grid gap-3 px-3 py-2.5 sm:grid-cols-2">
      {conContenido.map(([titulo, valores]) => (
        <div key={titulo}>
          <p className="mb-1 text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase">
            {titulo}
          </p>
          <ul className="list-disc space-y-0.5 pl-4 text-[12px] text-[var(--color-texto-suave)]">
            {valores.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export function TablaStakeholders({
  entidadId,
  filtros,
  onFiltros,
  onAbrir,
  seleccionados,
  onSeleccion,
}: {
  entidadId: number
  filtros: FiltrosTabla
  onFiltros: (f: FiltrosTabla) => void
  onAbrir: (id: number) => void
  seleccionados: RowSelectionState
  onSeleccion: (s: RowSelectionState) => void
}) {
  const enums = useEnums()
  const consulta = useTabla(entidadId, { ...filtros, page: filtros.page ?? 1 })
  const [expandida, setExpandida] = useState<number | null>(null)

  const columnas = useMemo<ColumnDef<StakeholderFila>[]>(
    () => [
      {
        id: 'seleccion',
        size: 32,
        header: () => null,
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`Seleccionar ${row.original.nombre}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            className="size-3.5 accent-[var(--color-acento)]"
          />
        ),
      },
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setExpandida(expandida === row.original.id ? null : row.original.id)
              }}
              className="shrink-0 text-[var(--color-texto-tenue)] hover:text-[var(--color-texto)]"
              aria-label={expandida === row.original.id ? 'Contraer' : 'Expandir'}
            >
              {expandida === row.original.id ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
            <span className="min-w-0 flex-1 font-medium">
              <CeldaTexto fila={row.original} entidadId={entidadId} campo="nombre">
                {row.original.nombre}
              </CeldaTexto>
            </span>
            {row.original.sigla && (
              <span className="shrink-0 text-[11px] text-[var(--color-texto-tenue)]">
                ({row.original.sigla})
              </span>
            )}
            {row.original.editado_por_usuario && <MarcaEdicion />}
            {row.original.corroborado && (
              <MarcaCorroborado corridas={row.original.n_corridas} />
            )}
          </div>
        ),
      },
      {
        accessorKey: 'tipo',
        header: 'Tipo',
        cell: ({ row }) => <EtiquetaTipo tipo={row.original.tipo} />,
      },
      {
        accessorKey: 'categoria',
        header: 'Categoría',
        meta: { clase: 'hidden xl:table-cell' },
        cell: ({ row }) => (
          <span className="block text-[var(--color-texto-suave)]">
            <CeldaTexto fila={row.original} entidadId={entidadId} campo="categoria">
              {row.original.categoria || '—'}
            </CeldaTexto>
          </span>
        ),
      },
      {
        accessorKey: 'nivel_influencia',
        header: 'Influencia',
        cell: ({ row }) => (
          <CeldaEnum
            fila={row.original}
            entidadId={entidadId}
            campo="nivel_influencia"
            opciones={enums.data?.nivel ?? []}
          >
            <EscaleraNivel nivel={row.original.nivel_influencia} titulo="Influencia" />
          </CeldaEnum>
        ),
      },
      {
        accessorKey: 'nivel_impacto_recibido',
        header: 'Impacto',
        meta: { clase: 'hidden lg:table-cell' },
        cell: ({ row }) => (
          <EscaleraNivel
            nivel={row.original.nivel_impacto_recibido}
            titulo="Impacto recibido"
          />
        ),
      },
      {
        accessorKey: 'postura',
        header: 'Postura',
        cell: ({ row }) => (
          <CeldaEnum
            fila={row.original}
            entidadId={entidadId}
            campo="postura"
            opciones={enums.data?.postura ?? []}
          >
            <EtiquetaPostura postura={row.original.postura} />
          </CeldaEnum>
        ),
      },
      {
        accessorKey: 'n_fuentes',
        header: 'Fuentes',
        cell: ({ row }) => (
          <span className="tabular block text-right text-[var(--color-texto-suave)]">
            {row.original.n_fuentes}
          </span>
        ),
      },
      {
        accessorKey: 'confianza',
        header: 'Confianza',
        meta: { clase: 'hidden lg:table-cell' },
        cell: ({ row }) => (
          <span className="tabular block text-right text-[var(--color-texto-suave)]">
            {row.original.confianza === null
              ? '—'
              : `${Math.round(row.original.confianza * 100)}%`}
          </span>
        ),
      },
    ],
    [expandida, entidadId, enums.data],
  )

  const tabla = useReactTable({
    data: consulta.data?.results ?? [],
    columns: columnas,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getRowId: (fila) => String(fila.id),
    state: { rowSelection: seleccionados },
    onRowSelectionChange: (actualizador) =>
      onSeleccion(
        typeof actualizador === 'function' ? actualizador(seleccionados) : actualizador,
      ),
    enableRowSelection: true,
  })

  const pagina = filtros.page ?? 1
  const total = consulta.data?.count ?? 0
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))
  const hayFiltros = Boolean(
    filtros.q || filtros.tipo?.length || filtros.postura?.length || filtros.nivel_minimo,
  )

  function cambiar(parcial: Partial<FiltrosTabla>) {
    // Cualquier cambio de filtro vuelve a la primera página: quedarse en la 4
    // de un resultado que ahora tiene 2 muestra una tabla vacía sin explicación.
    onFiltros({ ...filtros, ...parcial, page: 1 })
  }

  const alineadasDerecha = new Set(['n_fuentes', 'confianza'])
  const camposOrdenables: Record<string, string> = {
    nombre: 'nombre',
    tipo: 'tipo',
    categoria: 'categoria',
    nivel_influencia: 'nivel_influencia',
    nivel_impacto_recibido: 'nivel_impacto_recibido',
    postura: 'postura',
    n_fuentes: 'n_fuentes',
    confianza: 'confianza',
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Barra de filtros */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--color-borde)] px-3 py-2">
        <div className="relative min-w-56 flex-1 basis-64">
          <Search
            size={14}
            className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-[var(--color-texto-tenue)]"
          />
          <Entrada
            className="pl-8"
            placeholder="Buscar por nombre, sigla, categoría o descripción…"
            value={filtros.q ?? ''}
            onChange={(e) => cambiar({ q: e.target.value || undefined })}
          />
        </div>

        <Seleccion
          className="w-auto"
          value={filtros.tipo?.[0] ?? ''}
          onChange={(e) =>
            cambiar({ tipo: e.target.value ? [e.target.value as never] : undefined })
          }
          aria-label="Filtrar por tipo"
        >
          <option value="">Todos los tipos</option>
          {enums.data?.tipo.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
        </Seleccion>

        <Seleccion
          className="w-auto"
          value={filtros.postura?.[0] ?? ''}
          onChange={(e) =>
            cambiar({ postura: e.target.value ? [e.target.value as never] : undefined })
          }
          aria-label="Filtrar por postura"
        >
          <option value="">Todas las posturas</option>
          {enums.data?.postura.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
        </Seleccion>

        <Seleccion
          className="w-auto"
          value={filtros.nivel_minimo ?? ''}
          onChange={(e) =>
            cambiar({ nivel_minimo: (e.target.value || undefined) as Nivel | undefined })
          }
          aria-label="Influencia mínima"
        >
          <option value="">Cualquier influencia</option>
          {NIVELES.map((n) => (
            <option key={n} value={n}>
              {ETIQUETA_NIVEL[n]} o más
            </option>
          ))}
        </Seleccion>

        <Interruptor
          activo={Boolean(filtros.solo_corroborados)}
          onChange={(v) => cambiar({ solo_corroborados: v || undefined })}
        >
          Solo corroborados
        </Interruptor>

        {hayFiltros && (
          <Boton
            variante="sutil"
            icono={<X size={13} />}
            onClick={() => onFiltros({ page: 1 })}
          >
            Limpiar
          </Boton>
        )}
      </div>

      {/* Tabla */}
      <div className="scroll-sutil min-h-0 flex-1 overflow-auto">
        {consulta.isLoading ? (
          <EsqueletoTabla filas={10} columnas={7} />
        ) : consulta.isError ? (
          <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />
        ) : total === 0 ? (
          <Vacio
            titulo={hayFiltros ? 'Ningún stakeholder coincide' : 'Todavía no hay stakeholders'}
            detalle={
              hayFiltros
                ? 'Prueba a quitar algún filtro o a ampliar la búsqueda.'
                : 'Lanza una investigación para poblar esta tabla.'
            }
            accion={
              hayFiltros ? (
                <Boton variante="secundario" onClick={() => onFiltros({ page: 1 })}>
                  Limpiar filtros
                </Boton>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full min-w-[620px] table-fixed border-collapse text-[13px]">
            {/* Anchos explícitos: con `auto`, el nombre —lo único que se lee
                de verdad al escanear— quedaba estrangulado por columnas que
                solo llevan un icono. */}
            <colgroup>
              <col className="w-9" />
              <col />
              <col className="w-[150px]" />
              <col className="hidden w-[190px] xl:table-column" />
              <col className="w-[86px]" />
              <col className="hidden w-[86px] lg:table-column" />
              <col className="w-[112px]" />
              <col className="w-[72px]" />
              <col className="hidden w-[86px] lg:table-column" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-[var(--color-panel)]">
              <tr className="border-b border-[var(--color-borde)]">
                {tabla.getHeaderGroups()[0].headers.map((header) => {
                  const campo = camposOrdenables[header.column.id]
                  const derecha = alineadasDerecha.has(header.column.id)
                  return (
                    <th
                      key={header.id}
                      className={cx(
                        'px-3 py-2 font-semibold',
                        derecha ? 'text-right' : 'text-left',
                        header.column.id === 'seleccion' && 'w-8',
                        (header.column.columnDef.meta as MetaColumna | undefined)?.clase,
                      )}
                    >
                      {campo ? (
                        <CabeceraOrdenable
                          etiqueta={String(header.column.columnDef.header)}
                          campo={campo}
                          orden={filtros.ordering}
                          onOrden={(v) => onFiltros({ ...filtros, ordering: v })}
                          alineado={derecha ? 'right' : 'left'}
                        />
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {tabla.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr
                    onClick={() => onAbrir(row.original.id)}
                    className={cx(
                      'cursor-pointer border-b border-[var(--color-borde)] transition-colors',
                      row.getIsSelected()
                        ? 'bg-[var(--color-acento-suave)]'
                        : 'hover:bg-[var(--color-acento-suave)]',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={cx(
                          'px-3 py-2 align-middle',
                          (cell.column.columnDef.meta as MetaColumna | undefined)?.clase,
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {expandida === row.original.id && (
                    <tr className="border-b border-[var(--color-borde)] bg-[var(--color-lienzo)]">
                      <td colSpan={row.getVisibleCells().length}>
                        <DetalleExpandido fila={row.original} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {total > POR_PAGINA && (
        <div className="flex items-center justify-between gap-3 border-t border-[var(--color-borde)] px-3 py-2 text-[12px]">
          <span className="text-[var(--color-texto-suave)]">
            <span className="tabular">{(pagina - 1) * POR_PAGINA + 1}</span>–
            <span className="tabular">{Math.min(pagina * POR_PAGINA, total)}</span> de{' '}
            <span className="tabular">{total}</span>
          </span>
          <div className="flex items-center gap-1">
            <Boton
              variante="sutil"
              icono={<ChevronLeft size={14} />}
              disabled={pagina <= 1}
              onClick={() => onFiltros({ ...filtros, page: pagina - 1 })}
            >
              Anterior
            </Boton>
            <span className="tabular px-2 text-[var(--color-texto-suave)]">
              {pagina} / {paginas}
            </span>
            <Boton
              variante="sutil"
              disabled={pagina >= paginas}
              onClick={() => onFiltros({ ...filtros, page: pagina + 1 })}
            >
              Siguiente
              <Siguiente size={14} />
            </Boton>
          </div>
        </div>
      )}
    </div>
  )
}
